import PrintIcon from '@mui/icons-material/Print';
import RefreshIcon from '@mui/icons-material/Refresh';
import {LoadingButton} from '@mui/lab';
import {
  Alert,
  Backdrop,
  Box,
  Button,
  ButtonGroup,
  CircularProgress,
  Tooltip,
} from '@mui/material';
import dayjs from 'dayjs';
import {t} from 'i18next';
import update from 'immutability-helper';
import {useCallback, useEffect, useMemo, useRef, useState} from 'react';

import API, {getMessagesFromApiError} from '../../../api/axios';
import {apiBaseUrl} from '../../../api/urls';
import {useAppSelector} from '../../../hooks/redux';
import {useRefreshInterval} from '../../../hooks/refreshInterval';
import {DashboardPanelData} from '../../../interfaces/Dashboard';
import {
  EmployeeHistoryReportItem,
  EmployeeHistoryReportQuery,
  EmployeeHistoryReportResponse,
} from '../../../interfaces/EmployeeHistoryReport';
import {saveFile} from '../../../utils/file';
import {isPresent} from '../../../utils/type-guards';
import AssetHumanSelect from '../../asset-human/AssetHumanSelect';
import {AutoRefreshSelect} from '../../common/AutoRefreshSelect';
import DataGrid, {DataGridColumn, DataGridRef} from '../../common/DataGrid';
import ReportExportButton from '../../common/ReportExportButton';
import {
  usePanel,
  usePanelFilter,
} from '../../dashboards/entities/DashboardEntityContext';
import {DateRangeSelect} from '../../selectors/DateRangeSelect';
import {MachineInputSelectV2} from '../../selectors/MachineInputSelectV2';
import {ShiftSelect} from '../../selectors/ShiftSelect';
import {DashboardPanelTitleSlot} from '../DashboardPanelTitleSlot';

interface Props {
  value?: DashboardPanelData;
  onUpdate?: (value: DashboardPanelData) => void;
}

const DEFAULT_SHOWN_FIELDS = [
  'id',
  'alert_type',
  'personal_id',
  'first_name',
  'last_name',
  'human_type',
  'machine_name',
  'section',
  'date_start',
  'date_end',
  'time_in_zone',
];

export interface EmployeeHistoryReportData {
  refreshInterval?: number | null;
  params?: {
    page?: number;
    limit?: number;
    order?: string | null;
    dir?: 'ASC' | 'DESC';
    external_id?: number | null;
    shift_id?: number | null;
    date_start?: string | null;
    date_end?: string | null;
    input?: {
      [key: number]: 0 | 1 | null | undefined;
    };
  };
  columnWidths?: Record<string, number>;
  columnOrder?: string[];
  shownFields?: string[];
}

export const getEmployeeHistoryReportData = (): EmployeeHistoryReportData => ({
  params: {},
  columnWidths: {},
  columnOrder: undefined,
  shownFields: DEFAULT_SHOWN_FIELDS,
});

export const EmployeeHistoryReport = (props: Props) => {
  const [panel] = usePanel();
  const {filter: config, setFilter: setConfig} = usePanelFilter();

  const assetHumansSlice = useAppSelector(({assets}) => assets.asset_humans);
  const assetHumans = useMemo(
    () => assetHumansSlice.filter((i) => i.external_id),
    [assetHumansSlice]
  );

  /*********/
  /* fetch */
  /*********/

  const [fetchedData, setFetchedData] =
    useState<EmployeeHistoryReportResponse>();
  const [fetchedErrors, setFetchedErrors] = useState<string[]>([]);
  const [fetchedInProgress, setFetchedInProgress] = useState(false);

  const params = useMemo<EmployeeHistoryReportQuery>(
    () => ({
      page: config.params?.page ?? props.value?.params?.page ?? 0,
      limit: config.params?.limit ?? props.value?.params?.limit ?? 25,
      order: config.params?.order ?? props.value?.params?.order ?? null,
      dir: config.params?.dir ?? props.value?.params?.dir ?? null,
      external_id:
        config.params?.external_id ?? props.value?.params?.external_id ?? null,
      shift_id:
        config.params?.shift_id ?? props.value?.params?.shift_id ?? null,
      date_start:
        config.params?.date_start ??
        props.value?.params?.date_start ??
        dayjs().format('YYYY-MM-DD'),
      date_end:
        config.params?.date_end ??
        props.value?.params?.date_end ??
        dayjs().format('YYYY-MM-DD'),
      input: config.params?.input ?? props.value?.params?.input ?? {},
    }),
    [config]
  );

  const fetchData = useCallback(
    async (params: EmployeeHistoryReportQuery) => {
      setFetchedInProgress(true);
      setFetchedErrors([]);
      try {
        const endpoint = `${apiBaseUrl}/report/human-history`;
        const resp = await API.get<EmployeeHistoryReportResponse>(endpoint, {
          params: {
            ...params,
            input: JSON.stringify(params.input),
          },
        });
        setFetchedData(resp.data);
      } catch (error: any) {
        const messages = getMessagesFromApiError(error, "STRA_63a0795a");
        setFetchedErrors(messages);
      }
      setFetchedInProgress(false);
    },
    [params]
  );

  const selectedAssetHuman = useMemo(
    () =>
      params.external_id
        ? assetHumans.find((i) => i.external_id === params.external_id)
        : null,
    [params.external_id]
  );

  useEffect(() => {
    if (params) {
      fetchData(params);
      props.onUpdate?.({...config, params});
    }
  }, [params]);

  /****************/
  /* auto refresh */
  /****************/
  useRefreshInterval(() => fetchData(params), config?.refreshInterval);

  /*********/
  /* grid */
  /*********/
  const dataGridRef = useRef<DataGridRef>(null);
  const rows = fetchedData?.items ?? [];
  const columns: DataGridColumn<EmployeeHistoryReportItem>[] = [
    {
      field: 'id',
      headerName: 'ID',
      width: 60,
      resizeable: true,
      sortable: true,
    },
    {
      field: 'alert_type',
      headerName: 'Alert Type',
      width: 80,
      resizeable: true,
      sortable: true,
      valueGetter: ({row}) => row.alert_type ?? '',
    },
    {
      field: 'personal_id',
      headerName: 'Employee ID',
      width: 120,
      resizeable: true,
      sortable: true,
    },
    {
      field: 'first_name',
      headerName: 'First Name',
      width: 120,
      resizeable: true,
      sortable: true,
    },
    {
      field: 'last_name',
      headerName: 'Last Name',
      width: 120,
      resizeable: true,
      sortable: true,
    },
    {
      field: 'human_type',
      headerName: 'Employee Type',
      width: 120,
      resizeable: true,
      sortable: true,
    },
    {
      field: 'machine_name',
      headerName: 'Machine',
      width: 120,
      resizeable: true,
      sortable: true,
    },
    {
      field: 'section',
      headerName: 'Section',
      width: 120,
      resizeable: true,
      sortable: true,
    },
    {
      field: 'date_start',
      headerName: 'Start',
      width: 160,
      resizeable: true,
      sortable: true,
    },
    {
      field: 'date_end',
      headerName: 'End',
      width: 160,
      resizeable: true,
      sortable: true,
    },
    {
      field: 'time_in_zone',
      headerName: 'Time in Zone',
      width: 120,
      resizeable: true,
      sortable: true,
    },
  ];

  const exportFields = useMemo(
    () => {
      const fields = config?.shownFields ?? DEFAULT_SHOWN_FIELDS;
      return columns
        .filter((col) => !!col.headerName)
        .map((col) => ({
          field: col.field,
          label: col.headerName,
          hidden: !fields.includes(col.field),
        }));
    },
    [config?.shownFields]
  );

  /*********/
  /* export */
  /*********/
  const [exportInProgress, setExportInProgress] = useState(false);

  const shifts = useAppSelector(({assets}) => assets.shifts);
  const machineInputs = useAppSelector(({assets}) => assets.machine_inputs);

  const submitExport = async (params: EmployeeHistoryReportQuery) => {
    setExportInProgress(true);
    try {
      const endpoint = `${apiBaseUrl}/report/human-history-export`;
      const resp = await API.get(endpoint, {
        params: {
          ...params,
          input: JSON.stringify(params.input),
          export_type: 'line',
        },
        responseType: 'blob',
      });

      const fileNameParts = [
        'reportEmployeesHistory',
        `${params.date_start}__${params.date_end}`,
        shifts.find((i) => i.id === params.shift_id)?.name?.replace(' ', '-'),
        selectedAssetHuman?.name?.replace(' ', '-'),
        machineInputs
          .map((i) => {
            const v = params.input?.[i.id];
            if (v === 1) {
              return `${i.name.replace(' ', '-')}-is-on`;
            } else if (v === 0) {
              return `${i.name.replace(' ', '-')}-is-off`;
            }
          })
          .filter(isPresent)
          .join('_'),
      ].filter(isPresent);

      const fileName = `${fileNameParts.join('__')}.csv`;

      saveFile(resp.data, fileName);
    } catch (error: any) {
      const messages = getMessagesFromApiError(error, "STRA_be4d284e");
      setFetchedErrors(messages);
    }
    setExportInProgress(false);
  };

  const handleColumnWidthChange = (widths: Record<string, number>) => {
    setConfig?.(
      update(config, {
        columnWidths: {$set: widths},
      })
    );
  };

  const handleShownFieldsChange = useCallback(
    (fields: string[]) => {
      setConfig?.(
        update(config, {
          shownFields: {$set: fields},
        })
      );
    },
    [setConfig, config]
  );

  const handleColumnOrderAndShownFieldsChange = useCallback(
    (order: string[], fields: string[]) => {
      setConfig?.(
        update(config, {
          columnOrder: {$set: order},
          shownFields: {$set: fields},
        })
      );
    },
    [setConfig, config]
  );

  return (
    <Box
      display="flex"
      flexDirection="column"
      height="100%"
      gap={3}
      width="100%"
      overflow="hidden"
    >
      <DashboardPanelTitleSlot>
        {t(`panels.${panel?.code}`)}
      </DashboardPanelTitleSlot>

      <Backdrop open={fetchedInProgress} sx={{position: 'absolute'}}>
        <CircularProgress color="inherit" />
      </Backdrop>

      <Box display="flex" flexDirection="column">
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          gap={1}
        >
          <Box display="flex" overflow="auto" py={1} gap={1}>
            <Box minWidth={400}>
              <DateRangeSelect
                value={[
                  dayjs(params?.date_start).toDate(),
                  dayjs(params?.date_end).toDate(),
                ]}
                size="small"
                onChange={(v) => {
                  setConfig?.(
                    update(config, {
                      params: {
                        $set: {
                          ...params,
                          date_start: v?.[0]
                            ? dayjs(v?.[0]).format('YYYY-MM-DD')
                            : undefined,
                          date_end: v?.[0]
                            ? dayjs(v?.[1]).format('YYYY-MM-DD')
                            : undefined,
                          page: 0,
                        },
                      },
                    })
                  );
                }}
              />
            </Box>

            <AssetHumanSelect
              value={selectedAssetHuman?.id}
              size="small"
              label="Employee"
              fullWidth
              nullLabel="All Employees"
              assetHumans={assetHumans}
              sx={{minWidth: 200}}
              onChange={(_, item) => {
                setConfig?.(
                  update(config, {
                    params: {
                      $set: {
                        ...params,
                        external_id: item?.external_id,
                        page: 0,
                      },
                    },
                  })
                );
              }}
            />

            <ShiftSelect
              value={params?.shift_id}
              size="small"
              fullWidth
              nullLabel="All Shifts"
              sx={{minWidth: 200}}
              onChange={(v) => {
                setConfig?.(
                  update(config, {
                    params: {
                      $set: {
                        ...config.params,
                        shift_id: v,
                        page: 0,
                      },
                    },
                  })
                );
              }}
            />

            <Box minWidth={200}>
              <MachineInputSelectV2
                value={params?.input}
                label="Inputs"
                size="small"
                onChange={(v) => {
                  setConfig?.(
                    update(config, {
                      params: {
                        $set: {
                          ...params,
                          input: v,
                          page: 0,
                        },
                      },
                    })
                  );
                }}
              />
            </Box>
          </Box>

          <Box display="flex">
            <ButtonGroup>
              <Tooltip title="Reload">
                <LoadingButton
                  size="small"
                  loading={fetchedInProgress}
                  variant="outlined"
                  onClick={() => params && fetchData(params)}
                >
                  <RefreshIcon />
                </LoadingButton>
              </Tooltip>

              <AutoRefreshSelect
                value={config?.refreshInterval ?? null}
                onChange={(v) => {
                  setConfig?.(
                    update(config, {
                      refreshInterval: {
                        $set: v,
                      },
                    })
                  );
                }}
              />

              <ReportExportButton
                title="Export Human History"
                endpoint="/report/human-history"
                defaultFilename="employee_history.pdf"
                params={params as unknown as Record<string, unknown>}
                additionalParams={{input: JSON.stringify(params.input)}}
                exportFields={exportFields}
                onExportCsv={() => submitExport(params)}
                csvExportInProgress={exportInProgress}
              />

              <Tooltip title="Print">
                <Button onClick={() => dataGridRef.current?.printTable()}>
                  <PrintIcon />
                </Button>
              </Tooltip>
            </ButtonGroup>
          </Box>
        </Box>
      </Box>

      {fetchedErrors.map((error, idx) => (
        <Alert
          key={idx}
          severity="error"
          onClose={() => params && fetchData(params)}
        >
          {error}
        </Alert>
      ))}

      <DataGrid
        ref={dataGridRef}
        rows={rows}
        columns={columns}
        loading={fetchedInProgress}
        shownFields={config?.shownFields}
        defaultShownFields={DEFAULT_SHOWN_FIELDS}
        columnWidths={config?.columnWidths}
        columnOrder={config?.columnOrder}
        pagination
        paginationMode="server"
        size="small"
        sortBy={
          params?.order
            ? {
                field: params?.order,
                dir: params?.dir === 'DESC' ? 'desc' : 'asc',
              }
            : null
        }
        sortingMode="server"
        page={params?.page}
        pageSize={params?.limit}
        rowCount={fetchedData?.total}
        sxFooter={{
          bgcolor: (theme) =>
            theme.palette.mode === 'dark' ? '#2E2E2E' : '#FFF',
        }}
        onPageChange={(v) => {
          setConfig?.(
            update(config, {
              params: {
                $set: {
                  ...config.params,
                  page: v,
                },
              },
            })
          );
        }}
        onPageSizeChange={(v) => {
          setConfig?.(
            update(config, {
              params: {
                $set: {
                  ...config.params,
                  page: 0,
                  limit: v,
                },
              },
            })
          );
        }}
        onSort={(v) => {
          if (v) {
            setConfig?.(
              update(config, {
                params: {
                  $set: {
                    ...config.params,
                    order: v.field,
                    dir: v.dir === 'desc' ? 'DESC' : 'ASC',
                  },
                },
              })
            );
          }
        }}
        onShownFieldsChange={handleShownFieldsChange}
        onColumnWidthsChange={handleColumnWidthChange}
        onColumnOrderAndShownFieldsChange={handleColumnOrderAndShownFieldsChange}
      />
    </Box>
  );
};
